import type { ChangeEvent, FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Cookies } from '@trello/cookies';
import { convertToPIIString } from '@trello/privacy';

import {
  getDefaultLanguage,
  getTemplateLanguageEnabledFromMemberLocale,
} from 'app/src/components/Templates/Helpers';
import type { TemplateLanguage } from 'app/src/components/Templates/types';

import styles from './Footer.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import atlassianLogo from 'resources/images/atlassian-logo-gray-small.svg';

const footerLinks = [
  {
    key: 'templates',
    url: '/templates',
    message: (
      <FormattedMessage
        id="templates.footer.templates"
        defaultMessage="Templates"
        description="Templates"
      />
    ),
  },
  {
    key: 'pricing',
    url: '/pricing',
    message: (
      <FormattedMessage
        id="templates.footer.pricing"
        defaultMessage="Pricing"
        description="Pricing"
      />
    ),
  },
  {
    key: 'apps',
    url: '/platforms',
    message: (
      <FormattedMessage
        id="templates.footer.apps"
        defaultMessage="Apps"
        description="Apps"
      />
    ),
  },
  {
    key: 'jobs',
    url: '/jobs',
    message: (
      <FormattedMessage
        id="templates.footer.jobs"
        defaultMessage="Jobs"
        description="Jobs"
      />
    ),
  },
  {
    key: 'blog',
    url: 'https://blog.trello.com/',
    target: '_blank',
    message: (
      <FormattedMessage
        id="templates.footer.blog"
        defaultMessage="Blog"
        description="Blog"
      />
    ),
  },
  {
    key: 'developers',
    url: 'https://developers.trello.com',
    message: (
      <FormattedMessage
        id="templates.footer.developers"
        defaultMessage="Developers"
        description="Developers"
      />
    ),
  },
  {
    key: 'about',
    url: '/about',
    message: (
      <FormattedMessage
        id="templates.footer.about"
        defaultMessage="About"
        description="About"
      />
    ),
  },
  {
    key: 'help',
    url: 'https://help.trello.com',
    message: (
      <FormattedMessage
        id="templates.footer.help"
        defaultMessage="Help"
        description="Help"
      />
    ),
  },
  {
    key: 'legal',
    url: '/legal',
    message: (
      <FormattedMessage
        id="templates.footer.legal"
        defaultMessage="Legal"
        description="Legal"
      />
    ),
  },
  {
    key: 'privacy',
    url: '/privacy',
    message: (
      <FormattedMessage
        id="templates.footer.privacy"
        defaultMessage="Privacy"
        description="Privacy"
      />
    ),
  },
  {
    key: 'integrations',
    url: '/integrations',
    message: (
      <FormattedMessage
        id="templates.footer.integrations"
        defaultMessage="Integrations"
        description="Integrations"
      />
    ),
  },
  {
    key: 'contact',
    url: '/contact',
    message: (
      <FormattedMessage
        id="templates.footer.contact"
        defaultMessage="Contact us"
        description="Contact"
      />
    ),
  },
  {
    key: 'terms',
    url: 'https://www.atlassian.com/legal/cloud-terms-of-service',
    message: (
      <FormattedMessage
        id="templates.footer.terms"
        defaultMessage="Terms"
        description="Terms of Service"
      />
    ),
  },
];

// eslint-disable-next-line @trello/no-module-logic
const copyrightYear = new Date().getFullYear();

export const LanguagePicker: FunctionComponent<{
  languages?: TemplateLanguage[];
  templateLanguage?: TemplateLanguage;
}> = ({ languages, templateLanguage }) => {
  templateLanguage =
    templateLanguage ||
    getTemplateLanguageEnabledFromMemberLocale(
      convertToPIIString(Cookies.get('lang') || getDefaultLanguage().language),
      languages || [],
    );

  // don't show picker if we only have one enabled language
  // to pick from.
  if (!languages || languages.length <= 1) {
    return null;
  }

  const options = languages?.map(({ locale, localizedDescription }) => {
    return (
      <option value={locale} key={locale}>
        {localizedDescription}
      </option>
    );
  });

  const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
    Cookies.set('lang', e.target.value, {}, 'necessary');
    window.location.reload();
  };

  return (
    <div className={styles.languagePicker}>
      <select onChange={onChange} defaultValue={templateLanguage?.locale}>
        {options}
      </select>
    </div>
  );
};

export const Footer: FunctionComponent<{
  isLoggedOut?: boolean;
  languages?: TemplateLanguage[];
  templateLanguage?: TemplateLanguage;
}> = ({ isLoggedOut, languages, templateLanguage }) => {
  return (
    <div className={styles.footer}>
      {isLoggedOut && (
        <LanguagePicker
          languages={languages}
          templateLanguage={templateLanguage}
        />
      )}
      <ul className={styles.footerList}>
        {footerLinks.map((footerLink) => (
          <li className={styles.footerListItem} key={footerLink.key}>
            <a
              className={styles.footerListItemLink}
              href={footerLink.url}
              target={footerLink.target}
            >
              {footerLink.message}
            </a>
          </li>
        ))}
      </ul>

      <p className={styles.footerDetail}>
        <img
          alt=""
          role="presentation"
          className={styles.atlassianLogo}
          src={atlassianLogo}
        />
        <FormattedMessage
          id="templates.footer.copyright-all-rights-reserved"
          // eslint-disable-next-line formatjs/no-emoji
          defaultMessage="© Copyright {copyrightYear}. All rights reserved."
          description="Copyright"
          values={{ copyrightYear: copyrightYear.toString() }}
        />
      </p>
    </div>
  );
};
